import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Modal, Space, Spin, Typography, message } from "antd";
import { AimOutlined, EnvironmentOutlined, SearchOutlined } from "@ant-design/icons";
import { Autocomplete, GoogleMap, Marker } from "@react-google-maps/api";
import { mapPosition } from "../../models/map/mapCoordinates";

const DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 };
const MAP_OPTIONS = {
  clickableIcons: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  gestureHandling: "greedy",
};

export function coordinatesFromLocationText(value) {
  if (typeof value !== "string") return null;
  let text = value.trim();
  try { text = decodeURIComponent(text); } catch { /* Keep the original text. */ }

  const patterns = [
    /@(-?\d{1,2}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/,
    /(?:[?&](?:q|query|ll)=)(-?\d{1,2}(?:\.\d+)?),\s*(-?\d{1,3}(?:\.\d+)?)/i,
    /^\s*(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match) continue;
    const position = mapPosition(match[1], match[2]);
    if (position) return position;
  }
  return null;
}

function geocode(request) {
  return new Promise((resolve, reject) => {
    if (!window.google?.maps?.Geocoder) return reject(new Error("Google Maps is not ready."));
    new window.google.maps.Geocoder().geocode(request, (results, status) => {
      if (status === "OK" && results?.[0]) resolve(results[0]);
      else reject(new Error("Google could not find that location."));
    });
  });
}

export function LocationPickerModal({ open, onCancel, onSelect, value, mapsLoaded }) {
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState("");
  const [searchText, setSearchText] = useState("");
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initial = mapPosition(value?.latitude, value?.longitude);
    setPosition(initial);
    setAddress(value?.address || "");
    setSearchText(value?.address || "");
    setTimeout(() => {
      if (initial) mapRef.current?.panTo(initial);
    }, 0);
  }, [open, value?.address, value?.latitude, value?.longitude]);

  const choosePosition = async (nextPosition, preferredAddress = "") => {
    if (!nextPosition) return;
    setPosition(nextPosition);
    mapRef.current?.panTo(nextPosition);
    mapRef.current?.setZoom(Math.max(mapRef.current?.getZoom?.() || 16, 16));
    if (preferredAddress) {
      setAddress(preferredAddress);
      setSearchText(preferredAddress);
      return;
    }

    setResolving(true);
    try {
      const result = await geocode({ location: nextPosition });
      const label = result.formatted_address || `${nextPosition.lat}, ${nextPosition.lng}`;
      setAddress(label);
      setSearchText(label);
    } catch {
      const label = `Pinned location (${nextPosition.lat.toFixed(6)}, ${nextPosition.lng.toFixed(6)})`;
      setAddress(label);
      setSearchText(label);
    } finally {
      setResolving(false);
    }
  };

  const selectAutocompletePlace = () => {
    const place = autocompleteRef.current?.getPlace();
    const lat = place?.geometry?.location?.lat?.();
    const lng = place?.geometry?.location?.lng?.();
    const nextPosition = mapPosition(lat, lng);
    if (!nextPosition) return;
    choosePosition(nextPosition, place.formatted_address || place.name || searchText);
  };

  const search = async rawValue => {
    const text = String(rawValue || "").trim();
    if (!text) return;
    const coordinates = coordinatesFromLocationText(text);
    setResolving(true);
    try {
      if (coordinates) {
        await choosePosition(coordinates);
        return;
      }
      if (/^(?:https?:\/\/)?(?:maps\.app\.goo\.gl|goo\.gl\/maps)\//i.test(text)) {
        message.warning("This shortened Google Maps link cannot be resolved in the browser. Open it, copy its full address or coordinates, then search here—or place the pin manually.");
        return;
      }
      const result = await geocode({ address: text });
      const nextPosition = mapPosition(result.geometry?.location?.lat?.(), result.geometry?.location?.lng?.());
      if (!nextPosition) throw new Error("Google returned no map coordinates.");
      await choosePosition(nextPosition, result.formatted_address || text);
    } catch (error) {
      message.error(error.message || "Location could not be found.");
    } finally {
      setResolving(false);
    }
  };

  const confirm = () => {
    if (!position || !address.trim()) return;
    onSelect?.({ address: address.trim(), latitude: position.lat, longitude: position.lng });
  };

  return (
    <Modal
      width="min(1000px, calc(100vw - 32px))"
      title={<Space><EnvironmentOutlined />Choose task location</Space>}
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="select" type="primary" icon={<AimOutlined />} disabled={!position || !address.trim()} onClick={confirm}>Select destination</Button>,
      ]}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Search for an address or landmark, paste coordinates or a full Google Maps URL, or click the map and drag the pin to the exact entrance.
      </Typography.Paragraph>
      {!mapsLoaded ? (
        <div style={{ height: 480, display: "grid", placeItems: "center" }}><Spin size="large" tip="Loading Google Maps" /></div>
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Autocomplete
            onLoad={instance => { autocompleteRef.current = instance; }}
            onPlaceChanged={selectAutocompletePlace}
            fields={["formatted_address", "geometry", "name"]}
          >
            <Input.Search
              size="large"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              onSearch={search}
              enterButton="Find location"
              loading={resolving}
              placeholder="Search an address, landmark, coordinates, or full Maps URL"
            />
          </Autocomplete>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: 430, borderRadius: 10 }}
            center={position || DEFAULT_CENTER}
            zoom={position ? 16 : 11}
            options={MAP_OPTIONS}
            onLoad={map => { mapRef.current = map; }}
            onClick={event => choosePosition({ lat: event.latLng.lat(), lng: event.latLng.lng() })}
          >
            {position && (
              <Marker
                position={position}
                draggable
                onDragEnd={event => choosePosition({ lat: event.latLng.lat(), lng: event.latLng.lng() })}
              />
            )}
          </GoogleMap>
          <Input
            value={address}
            onChange={event => setAddress(event.target.value)}
            placeholder="Address label or helpful directions"
            addonBefore="Selected address"
          />
          <Alert
            type={position ? "success" : "info"}
            showIcon
            message={position ? "Map location selected" : "Search or click the map to place the destination pin"}
            description={position ? `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}` : undefined}
          />
        </Space>
      )}
    </Modal>
  );
}
